import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSelect } from '../components/LanguageSelect';
import { ChainSelect } from '../components/form/ChainSelect';
import { WalletSelect } from '../components/form/WalletSelect';
import {
  inputFieldClass,
  inputMonoFieldClass,
  labelClass,
  primarySubmitButtonClass,
  selectFieldClass,
} from '../components/form/fieldClasses';
import {
  getJSON,
  postJSON,
  type CatalogResponse,
  type TransferNetworkResponse,
  type TransferPendingNonceResponse,
  type TransferTransactionInfo,
  type TransferPrepareResponse,
  type TransferSubmitResponse,
  type UserProfile,
  type WalletsResponse,
  type WatchedTokenBalancesResponse,
  userWalletApiBase,
} from '../lib/api';
import { explorerTxUrl } from '../lib/explorers';
import { prefillEip1559Gwei } from '../lib/gasFees';
import { useWalletSessionUnlocked } from '../hooks/useWalletSessionUnlocked';
import { isWalletSessionUnlocked } from '../lib/mpcUnlockCache';
import { signDigestWithWalletMpc } from '../lib/mpcSession';
import { appMainClass, pageWrapClass } from '../layout/page';

/** Undefined = omit (server uses pending nonce); throws if non-empty but invalid. */
function parseNonceInput(raw: string): number | undefined {
  const t = raw.trim();
  if (!t) {
    return undefined;
  }
  if (!/^\d+$/.test(t)) {
    throw new Error('INVALID_NONCE');
  }
  const n = Number(t);
  if (!Number.isSafeInteger(n) || n < 0) {
    throw new Error('INVALID_NONCE');
  }
  return n;
}

export default function TransferPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [wallets, setWallets] = useState<WalletsResponse['wallets']>([]);
  const [catalog, setCatalog] = useState<CatalogResponse['tokens']>([]);
  const [walletId, setWalletId] = useState<number>(0);
  const payUnlocked = useWalletSessionUnlocked(walletId);
  const [chain, setChain] = useState<string>('ethereum');
  const [toAddress, setToAddress] = useState('');
  const [tokenRef, setTokenRef] = useState('native');
  const [amount, setAmount] = useState('');
  const [maxFeeGwei, setMaxFeeGwei] = useState('2');
  const [priorityFeeGwei, setPriorityFeeGwei] = useState('1');
  const [paymentPassword, setPaymentPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [profile, setProfile] = useState<Pick<UserProfile, 'mfa_enabled'> | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgOk, setMsgOk] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [baseFeeLine, setBaseFeeLine] = useState('');
  const [sellBalanceLine, setSellBalanceLine] = useState('');
  const [builtTxJson, setBuiltTxJson] = useState<TransferTransactionInfo | null>(null);
  const [nonceInput, setNonceInput] = useState('');
  const [nonceLoadErr, setNonceLoadErr] = useState('');
  const feeReqGen = useRef(0);
  const balReqGen = useRef(0);
  const nonceReqGen = useRef(0);
  const nonceDirtyRef = useRef(false);

  const loadBootstrap = useCallback(async () => {
    setLoading(true);
    setMsg('');
    try {
      const [wr, cr, pr] = await Promise.all([
        getJSON<WalletsResponse>('/api/user/wallets'),
        getJSON<CatalogResponse>('/api/user/swap/catalog'),
        getJSON<UserProfile>('/api/user/profile'),
      ]);
      if (wr.res.status === 401 || pr.res.status === 401) {
        navigate('/', { replace: true });
        return;
      }
      if (wr.res.status === 403 || pr.res.status === 403) {
        navigate('/home', { replace: true });
        return;
      }
      if (pr.res.ok) {
        setProfile({ mfa_enabled: pr.data.mfa_enabled });
      } else {
        setProfile(null);
      }
      if (!wr.res.ok) {
        setMsgOk(false);
        setMsg(wr.data.error || t('common.error'));
        setWallets([]);
      } else {
        setWallets(wr.data.wallets ?? []);
      }
      setCatalog(cr.res.ok ? (cr.data.tokens ?? []) : []);
      if (!cr.res.ok) {
        setMsgOk(false);
        setMsg((m) => m || cr.data.error || t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    void loadBootstrap();
  }, [loadBootstrap, i18n.language]);

  useEffect(() => {
    const raw = searchParams.get('wallet');
    const n = raw ? Number(raw) : NaN;
    if (!Number.isFinite(n) || n <= 0 || wallets.length === 0) {
      return;
    }
    if (!wallets.some((w) => w.id === n)) {
      return;
    }
    setWalletId(n);
  }, [searchParams, wallets]);

  const tokensOnChain = useMemo(
    () => (catalog ?? []).filter((c) => c.chain === chain).sort((a, b) => a.symbol.localeCompare(b.symbol)),
    [catalog, chain],
  );

  useEffect(() => {
    if (tokensOnChain.length === 0) {
      setTokenRef('native');
      return;
    }
    if (!tokensOnChain.some((x) => x.token_ref === tokenRef)) {
      setTokenRef(tokensOnChain[0].token_ref);
    }
  }, [tokensOnChain, tokenRef]);

  useEffect(() => {
    nonceDirtyRef.current = false;
  }, [walletId, chain]);

  useEffect(() => {
    if (!walletId || !chain) {
      setNonceInput('');
      setNonceLoadErr('');
      return;
    }
    const gen = ++nonceReqGen.current;
    setNonceLoadErr('');
    void (async () => {
      const { res, data } = await postJSON<TransferPendingNonceResponse>('/api/user/transfer/pending-nonce', {
        wallet_id: walletId,
        chain,
      });
      if (gen !== nonceReqGen.current) {
        return;
      }
      if (!res.ok) {
        setNonceLoadErr(data.error || t('transfer.nonceLoadError'));
        return;
      }
      if (nonceDirtyRef.current) {
        return;
      }
      const n = data.nonce;
      if (typeof n === 'number' && Number.isFinite(n)) {
        setNonceInput(String(n));
      }
    })();
  }, [walletId, chain, t]);

  useEffect(() => {
    const gen = ++feeReqGen.current;
    setBaseFeeLine('');
    void (async () => {
      const { res, data } = await postJSON<TransferNetworkResponse>('/api/user/transfer/network', { chain });
      if (gen !== feeReqGen.current) {
        return;
      }
      if (!res.ok) {
        setBaseFeeLine(data.error || t('common.error'));
        return;
      }
      const suggested = prefillEip1559Gwei(data.gas_price_gwei, data.base_fee_gwei);
      if (suggested) {
        setMaxFeeGwei(suggested.maxFeeGwei);
        setPriorityFeeGwei(suggested.priorityFeeGwei);
      }
      const base = data.base_fee_gwei?.trim() || '0';
      setBaseFeeLine(t('transfer.baseFeeLine', { gwei: base }));
    })();
  }, [chain, t]);

  useEffect(() => {
    if (!walletId || !chain || !tokenRef) {
      setSellBalanceLine('');
      return;
    }
    const gen = ++balReqGen.current;
    setSellBalanceLine(t('transfer.balanceLoading'));
    void (async () => {
      const { res, data } = await postJSON<WatchedTokenBalancesResponse>(`${userWalletApiBase(walletId)}/tokens/balances`, {
        tokens: [{ chain, token_ref: tokenRef }],
      });
      if (gen !== balReqGen.current) {
        return;
      }
      if (!res.ok) {
        setSellBalanceLine(data.error || t('common.error'));
        return;
      }
      const row = data.balances?.[0];
      if (!row) {
        setSellBalanceLine('');
        return;
      }
      if (row.balance_error) {
        setSellBalanceLine(`${t('transfer.balance')}: ${row.balance_error}`);
        return;
      }
      const sym = row.symbol?.trim() ?? '';
      const amt = row.balance_formatted?.trim() ?? '0';
      setSellBalanceLine(sym ? `${t('transfer.balance')}: ${amt} ${sym}` : `${t('transfer.balance')}: ${amt}`);
    })();
  }, [walletId, chain, tokenRef, t]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!walletId) {
      return;
    }
    setSubmitting(true);
    setMsg('');
    setTxHash('');
    setBuiltTxJson(null);
    try {
      const payPwd = paymentPassword.trim();
      if (!payPwd && !payUnlocked && !(await isWalletSessionUnlocked(walletId))) {
        throw new Error(t('transfer.paymentPasswordRequired'));
      }
      let nonceExplicit: number | undefined;
      try {
        nonceExplicit = parseNonceInput(nonceInput);
      } catch {
        throw new Error(t('transfer.nonceInvalid'));
      }
      const prepBody: Record<string, unknown> = {
        wallet_id: walletId,
        chain,
        to_address: toAddress.trim(),
        token_ref: tokenRef,
        amount: amount.trim(),
        max_fee_gwei: maxFeeGwei.trim(),
        priority_fee_gwei: priorityFeeGwei.trim(),
      };
      if (nonceExplicit !== undefined) {
        prepBody.nonce = nonceExplicit;
      }
      const { res: prepRes, data: prep } = await postJSON<TransferPrepareResponse>(
        '/api/user/transfer/prepare',
        prepBody,
      );
      if (!prepRes.ok) {
        throw new Error(prep.error || t('common.error'));
      }
      if (prep.transaction) {
        flushSync(() => setBuiltTxJson(prep.transaction!));
      } else {
        setBuiltTxJson(null);
      }
      const signOut = await signDigestWithWalletMpc(walletId, prep.digest_hex, payPwd, {
        commonErrorMessage: t('common.error'),
        sessionMissingMessage: t('transfer.sessionMissing'),
        sessionInvalidMessage: t('transfer.sessionInvalid'),
        mfaCode: profile?.mfa_enabled ? mfaCode.trim() : undefined,
      });
      const nonceForSubmit = nonceExplicit ?? prep.nonce;
      const { res: submitRes, data: submitData } = await postJSON<TransferSubmitResponse>('/api/user/transfer/submit', {
        wallet_id: walletId,
        chain,
        to_address: toAddress.trim(),
        token_ref: tokenRef,
        amount: amount.trim(),
        max_fee_gwei: maxFeeGwei.trim(),
        priority_fee_gwei: priorityFeeGwei.trim(),
        nonce: nonceForSubmit,
        gas_limit: prep.gas_limit,
        r_hex: signOut.r_hex,
        s_hex: signOut.s_hex,
        v: signOut.v,
      });
      if (!submitRes.ok) {
        throw new Error(submitData.error || t('common.error'));
      }
      setMsgOk(true);
      setMsg(submitData.message || t('transfer.submitted'));
      setTxHash(submitData.tx_hash ?? '');
      setPaymentPassword('');
      setMfaCode('');
    } catch (err) {
      setMsgOk(false);
      setMsg(String(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-slate-100 text-slate-600">{t('home.loading')}</div>
    );
  }

  const txLink = txHash ? explorerTxUrl(chain, txHash) : null;

  return (
    <div className={pageWrapClass}>
      <main className={appMainClass}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link to="/home" className="text-sm font-medium text-blue-600 hover:underline">
            ← {t('transfer.backHome')}
          </Link>
          <LanguageSelect />
        </div>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-semibold text-slate-900">{t('transfer.title')}</h1>
          <p className="mt-1 text-sm text-slate-600">{t('transfer.subtitle')}</p>

          {msg ? (
            <div
              role="status"
              className={`mt-4 rounded-xl border px-4 py-3 text-sm font-medium ${
                msgOk ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-red-200 bg-red-50 text-red-800'
              }`}
            >
              {msg}
            </div>
          ) : null}

          {builtTxJson ? (
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h2 className="text-sm font-semibold text-slate-800">{t('transfer.txJsonTitle')}</h2>
              <p className="mt-1 text-xs text-slate-600">{t('transfer.txJsonHint')}</p>
              <pre className="mt-3 max-h-80 overflow-auto rounded-lg border border-slate-200 bg-white p-3 text-xs leading-relaxed text-slate-800">
                {JSON.stringify(builtTxJson, null, 2)}
              </pre>
            </div>
          ) : null}

          {txHash ? (
            <p className="mt-3 break-all text-sm text-slate-700">
              {t('transfer.txHash')}:&nbsp;
              {txLink ? (
                <a href={txLink} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">
                  {txHash}
                </a>
              ) : (
                <span className="font-mono">{txHash}</span>
              )}
            </p>
          ) : null}

          <form className="mt-6 space-y-4" onSubmit={(e) => void onSubmit(e)}>
            <div>
              <label htmlFor="transfer-wallet" className={labelClass}>
                {t('transfer.selectWallet')}
              </label>
              <WalletSelect
                id="transfer-wallet"
                wallets={wallets}
                value={walletId}
                onChange={setWalletId}
                placeholder={t('transfer.walletPlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="transfer-chain" className={labelClass}>
                {t('transfer.chain')}
              </label>
              <ChainSelect id="transfer-chain" value={chain} onChange={setChain} />
            </div>

            <div>
              <label htmlFor="transfer-to" className={labelClass}>
                {t('transfer.toAddress')}
              </label>
              <input
                id="transfer-to"
                type="text"
                autoComplete="off"
                spellCheck={false}
                className={inputMonoFieldClass}
                placeholder="0x..."
                value={toAddress}
                onChange={(e) => setToAddress(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="transfer-token" className={labelClass}>
                {t('transfer.token')}
              </label>
              <select
                id="transfer-token"
                className={selectFieldClass}
                value={tokenRef}
                onChange={(e) => setTokenRef(e.target.value)}
              >
                {tokensOnChain.map((tok) => (
                  <option key={`${tok.chain}-${tok.token_ref}`} value={tok.token_ref}>
                    {tok.symbol} — {tok.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <label htmlFor="transfer-amount" className={labelClass}>
                  {t('transfer.amount')}
                </label>
                {walletId > 0 && sellBalanceLine ? <span className="text-xs text-slate-500">{sellBalanceLine}</span> : null}
              </div>
              <input
                id="transfer-amount"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                spellCheck={false}
                className={inputMonoFieldClass}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="transfer-nonce" className={labelClass}>
                {t('transfer.nonce')}
              </label>
              <input
                id="transfer-nonce"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                spellCheck={false}
                className={inputMonoFieldClass}
                placeholder="0"
                value={nonceInput}
                onChange={(e) => {
                  nonceDirtyRef.current = true;
                  setNonceInput(e.target.value);
                }}
              />
              <p className="mt-1 text-xs text-slate-500">{t('transfer.nonceHint')}</p>
              {nonceLoadErr ? <p className="mt-1 text-xs text-amber-700">{nonceLoadErr}</p> : null}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="transfer-max-fee" className={labelClass}>
                  {t('transfer.maxFee')}
                </label>
                <input
                  id="transfer-max-fee"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  spellCheck={false}
                  className={inputMonoFieldClass}
                  value={maxFeeGwei}
                  onChange={(e) => setMaxFeeGwei(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="transfer-priority-fee" className={labelClass}>
                  {t('transfer.priorityFee')}
                </label>
                <input
                  id="transfer-priority-fee"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  spellCheck={false}
                  className={inputMonoFieldClass}
                  value={priorityFeeGwei}
                  onChange={(e) => setPriorityFeeGwei(e.target.value)}
                />
              </div>
            </div>
            {baseFeeLine ? <p className="text-xs text-slate-500">{baseFeeLine}</p> : null}

            <div>
              <label htmlFor="transfer-paypwd" className={labelClass}>
                {t('transfer.paymentPassword')}
              </label>
              <input
                id="transfer-paypwd"
                required={walletId > 0 ? !payUnlocked : true}
                type="password"
                autoComplete="current-password"
                className={inputFieldClass}
                value={paymentPassword}
                onChange={(e) => setPaymentPassword(e.target.value)}
                placeholder={walletId > 0 && payUnlocked ? t('transfer.payPwdUnlockHint') : undefined}
              />
              {walletId > 0 && payUnlocked ? (
                <p className="mt-1 text-xs text-slate-500">{t('transfer.payPwdUnlockHint')}</p>
              ) : null}
            </div>

            {profile?.mfa_enabled ? (
              <div>
                <label htmlFor="transfer-mfa" className={labelClass}>
                  {t('mfa.code')}
                </label>
                <input
                  id="transfer-mfa"
                  required
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className={inputFieldClass}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                />
              </div>
            ) : null}

            <button
              type="submit"
              disabled={submitting || !walletId || tokensOnChain.length === 0}
              className={primarySubmitButtonClass}
            >
              {submitting ? t('transfer.submitting') : t('transfer.submit')}
            </button>
          </form>
        </section>
      </main>
    </div>
  );
}
